import { useState, useEffect, type CSSProperties } from 'react';
import { useT } from '../../i18n/index.jsx';
import Icon from '../ui/Icon';
import { S } from '../../config/theme.js';
import { safeGetLS, safeSetLS } from '../../lib/storage.js';

/**
 * Custom Event-Type für `beforeinstallprompt` — ist nicht in Standard-TS-DOM-Typen
 * enthalten, weil der Event nur Chromium-basierte Browser feuern. Die Shape
 * entspricht dem MDN-Spec.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

// PWA Install: beforeinstallprompt Event cachen
let _deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', function (e: Event) {
    e.preventDefault();
    _deferredInstallPrompt = e as BeforeInstallPromptEvent;
  });
}

/**
 * PwaInstallBanner — Banner mit App-Installations-Aufforderung.
 *
 * Nutzt den zwischengespeicherten `beforeinstallprompt`-Event und zeigt den
 * Banner nur, wenn der Browser die Installation anbietet und der User den
 * Banner nicht in den letzten 7 Tagen weggeklickt hat.
 */
export function PwaInstallBanner() {
  const t = useT();
  const [show, setShow] = useState<boolean>(false);
  const [dismissed, setDismissed] = useState<boolean>(function () {
    const ts = safeGetLS('drpx_pwa_dismissed', '');
    if (!ts) return false;
    return Date.now() - Number(ts) < 604800000;
  });

  useEffect(
    function () {
      const check = function () {
        if (_deferredInstallPrompt && !dismissed) setShow(true);
      };
      check();
      const iv = setInterval(check, 3000);
      return function () {
        clearInterval(iv);
      };
    },
    [dismissed],
  );

  if (!show || dismissed) return null;

  const install = function () {
    if (_deferredInstallPrompt) {
      void _deferredInstallPrompt.prompt();
      void _deferredInstallPrompt.userChoice.then(function () {
        _deferredInstallPrompt = null;
        setShow(false);
      });
    }
  };
  const dismiss = function () {
    safeSetLS('drpx_pwa_dismissed', String(Date.now()));
    setDismissed(true);
  };

  return (
    <div
      style={{
        ...(S.row as CSSProperties),
        position: 'fixed' as const,
        bottom: 70,
        left: 12,
        right: 12,
        zIndex: 150,
        background: 'linear-gradient(135deg,#1f2e22,#2d4a32)',
        border: '1px solid rgba(74,222,128,.3)',
        borderRadius: 16,
        padding: '14px 18px',
        gap: 12,
        boxShadow: '0 8px 32px rgba(0,0,0,.3)',
        animation: 'slideUpModal .3s ease',
        color: '#fff',
        fontFamily: "'DM Sans',sans-serif",
      }}>
      <div style={{ ...(S.row as CSSProperties), flexShrink: 0 }}>
        <Icon name="rocket" size={28} color="#fff" />
      </div>
      <div className="flex-1">
        <div
          style={{ ...(S.labelLg as CSSProperties), fontWeight: 700, marginBottom: 2 }}>
          {t('pwaInstallBtn')}
        </div>
        <div style={{ fontSize: 11, opacity: 0.7 }}>
          {t('pwaInstallHint')}
        </div>
      </div>
      <button
        onClick={install}
        style={{
          ...(S.labelMd as CSSProperties),
          background: 'var(--accent)',
          color: '#fff',
          border: 'none',
          borderRadius: 10,
          padding: '8px 16px',
          cursor: 'pointer',
          flexShrink: 0,
        }}>
        {t('pwaInstallBtn')}
      </button>
      <button
        onClick={dismiss}
        style={{
          background: 'none',
          border: 'none',
          color: 'rgba(255,255,255,.4)',
          fontSize: 16,
          cursor: 'pointer',
          padding: '2px 6px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
        }}>
        <Icon name="cross" size={14} color="rgba(255,255,255,.4)" />
      </button>
    </div>
  );
}
